// src/components/property/NegativePromptSection.tsx
/**
 * NegativePromptSection Component
 * Collapsible advanced section for editing negative prompts
 */

import { memo, useState } from 'react';
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { INegativePromptSectionProps } from '@/@types/promptTemplates';

function NegativePromptSection({
  value,
  defaultValue,
  onChange,
  disabled = false,
}: INegativePromptSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChanges = value !== defaultValue;

  const handleReset = () => {
    onChange(defaultValue);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
        >
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span>Advanced (Negative Prompt)</span>
          {hasChanges && <span className="ml-auto text-xs text-amber-500">Modified</span>}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="negative-prompt-textarea" className="text-sm text-muted-foreground">
              Things to avoid in the generated video
            </label>
            {hasChanges && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                disabled={disabled}
                className="h-7 text-xs"
              >
                <RotateCcw className="mr-1 h-3 w-3" />
                Reset
              </Button>
            )}
          </div>
          <Textarea
            id="negative-prompt-textarea"
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={disabled}
            rows={3}
            className="text-sm"
            placeholder="Enter things to avoid..."
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export const NegativePromptSectionMemo = memo(NegativePromptSection);
